import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { publicApiClient } from '@/api/public'
import { CalendarDays, Clock, MapPin, Users, ArrowLeft, CheckCircle2, ExternalLink, CreditCard, Globe } from 'lucide-react'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'

function isColourDark(hex: string): boolean {
  try {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return (r * 299 + g * 587 + b * 114) / 1000 < 128
  } catch { return true }
}

export default function PublicBookingViewPage() {
  const { slug, bookingId } = useParams<{ slug: string; bookingId: string }>()
  const [searchParams] = useSearchParams()
  const paymentSuccess = searchParams.get('payment') === 'success'

  // We need the tenant branding — reuse the sessions list endpoint for that
  const { data: sessionsData } = useQuery({
    queryKey: ['public-sessions', slug],
    queryFn: () => publicApiClient.getPublicSessions(slug!).then(r => r.data),
    enabled: !!slug,
  })

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-booking', slug, bookingId],
    queryFn: () => publicApiClient.getPublicBooking(slug!, bookingId!).then(r => r.data),
    enabled: !!slug && !!bookingId,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency ?? sessionsData?.currency)
  const { formatDate, formatTime } = datesFor(data?.region, data?.timeZoneId)
  const fmtDate = (iso: string) => {
    return formatDate(iso, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  }
  const fmtTime = (iso: string) => {
    return formatTime(iso)
  }

  const tenantName    = sessionsData?.tenantName ?? ''
  const tenantLogoUrl = sessionsData?.tenantLogoUrl ?? null
  const primaryColour = sessionsData?.primaryColour ?? null

  const hasPrimary = !!primaryColour
  const darkBg     = hasPrimary ? isColourDark(primaryColour!) : false
  const headerStyle = hasPrimary ? { backgroundColor: primaryColour! } : undefined
  const headerClass = hasPrimary ? '' : 'bg-white border-b border-slate-200'
  const nameClass   = hasPrimary ? (darkBg ? 'text-white' : 'text-slate-900') : 'text-slate-800'

  // Declared before the early returns below so hook order stays stable (Rules of Hooks).
  const checkoutMutation = useMutation({
    mutationFn: () => publicApiClient.createSessionBookingCheckout(slug!, bookingId!).then(r => r.data),
    onSuccess: ({ url }) => { window.location.href = url },
  })

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (isError || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-700">Booking not found</p>
        <Link to={`/sessions/${slug}`} className="text-sm text-indigo-500 mt-2 block">Back to all sessions</Link>
      </div>
    </div>
  )

  const isCancelled = data.status === 'Cancelled'
  const isPending = data.status === 'PendingPayment'
  const venue = data.venueName
    ? [data.venueName, data.venueCity, data.venuePostcode].filter(Boolean).join(', ')
    : null

  const showPayment = !data.isPaid && !isCancelled && data.totalAmount != null && data.totalAmount > 0
    && (data.stripeConnectEnabled || !!data.paymentLink)

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className={headerClass} style={headerStyle}>
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-4">
          {tenantLogoUrl && (
            <img src={tenantLogoUrl} alt={tenantName} className="h-8 w-auto object-contain" />
          )}
          {tenantName && (
            <span className={`font-semibold ${nameClass}`}>{tenantName}</span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <Link to={`/sessions/${slug}`} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft size={15} /> All sessions
        </Link>

        {/* Status badge */}
        <div className="flex items-center gap-3">
          <CheckCircle2 size={22} className={isCancelled ? 'text-slate-400' : isPending ? 'text-amber-500' : 'text-green-500'} />
          <div>
            <p className="font-semibold text-slate-900">
              {isCancelled ? 'Booking cancelled' : isPending ? 'Payment required' : 'Booking confirmed'}
            </p>
            <p className="text-sm text-slate-500">
              {isPending
                ? 'Your place is reserved — complete payment below to confirm it.'
                : <>Reference: <span className="font-mono text-slate-700">{data.bookingId.slice(0, 8).toUpperCase()}</span></>}
            </p>
          </div>
        </div>

        {/* Payment success banner */}
        {paymentSuccess && (
          <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            <CheckCircle2 size={16} className="text-green-600 shrink-0" />
            Payment received — thank you!
          </div>
        )}

        {/* Payment section */}
        {showPayment && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-semibold text-slate-900 mb-1">Payment</h2>
            <p className="text-sm text-slate-500 mb-4">
              Total due: <span className="font-semibold text-slate-900">{fmtMoney(data.totalAmount!)}</span>
            </p>
            {data.stripeConnectEnabled ? (
              <button
                onClick={() => checkoutMutation.mutate()}
                disabled={checkoutMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer"
              >
                <CreditCard size={15} />
                {checkoutMutation.isPending ? 'Redirecting…' : 'Pay securely with card'}
              </button>
            ) : data.paymentLink ? (
              <a
                href={data.paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors cursor-pointer"
              >
                <CreditCard size={15} /> Pay now
              </a>
            ) : null}
          </div>
        )}

        {/* Paid badge — hidden right after payment, where the thank-you banner already covers it */}
        {data.isPaid && !paymentSuccess && (
          <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            <CheckCircle2 size={16} className="text-green-600 shrink-0" />
            Payment complete
          </div>
        )}

        {/* Session details */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h1 className="text-xl font-bold text-slate-900">{data.courseName}</h1>
            {data.totalAmount !== null && (
              <span className="text-xl font-bold text-slate-900 shrink-0">{fmtMoney(data.totalAmount)}</span>
            )}
          </div>

          <div className="space-y-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <CalendarDays size={15} className="text-slate-400" />
              {fmtDate(data.startsAt)}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={15} className="text-slate-400" />
              {fmtTime(data.startsAt)}
              {data.endsAt && ` – ${fmtTime(data.endsAt)}`}
            </div>
            {data.deliveryMethod === 'Online' ? (
              <div className="flex items-start gap-2">
                <Globe size={15} className="text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <span>Online</span>
                  {data.meetingUrl && !isCancelled && (
                    <>
                      {' — '}
                      <a href={data.meetingUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">
                        {data.meetingUrl}
                      </a>
                    </>
                  )}
                  {data.meetingInstructions && !isCancelled && (
                    <p className="text-slate-500 mt-1 whitespace-pre-line">{data.meetingInstructions}</p>
                  )}
                </div>
              </div>
            ) : venue ? (
              <div className="flex items-center gap-2">
                <MapPin size={15} className="text-slate-400" />
                {venue}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin size={15} /> Venue to be confirmed
              </div>
            )}
            <div className="flex items-center gap-2">
              <Users size={15} className="text-slate-400" />
              {data.placesRequested} place{data.placesRequested !== 1 ? 's' : ''}
            </div>
          </div>

          {data.courseWebsiteUrl && (
            <a
              href={data.courseWebsiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"
            >
              <ExternalLink size={14} /> More about this course
            </a>
          )}
        </div>

        {/* Attendees */}
        {data.attendees.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-semibold text-slate-900 mb-3">Attendees</h2>
            <div className="space-y-2">
              {data.attendees.map((a, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-indigo-600">{i + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{a.fullName}</p>
                    {a.companyName && <p className="text-slate-500">{a.companyName}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact details */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-semibold text-slate-900 mb-3">Booking contact</h2>
          <div className="space-y-1 text-sm text-slate-700">
            <p className="font-medium">{data.contactName}</p>
            <p className="text-slate-500">{data.contactEmail}</p>
            {data.contactPhone && <p className="text-slate-500">{data.contactPhone}</p>}
          </div>
        </div>

        {data.notes && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-semibold text-slate-900 mb-2">Notes</h2>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{data.notes}</p>
          </div>
        )}
      </div>
    </div>
  )
}
